import { Component, Element, Event, EventEmitter, Method, Prop, Watch, h } from '@stencil/core';
import { focusElement, generateId, isInError, getRequiredInd, ariaDescribedby } from '../../utils/functions';

/**
 * Selects are used to capture a user choice within a set of related options.
 */
/**
 * @slot label - Label for the select. Alternatively, you can use the label attribute.
 */
@Component({
  tag: 'som-select',
  styleUrl: 'som-select.scss',
})
export class SomSelect {
  @Element() el: HTMLElement;

  /**
   * This property can be used to set a label.
   */
  @Prop() label: string = '';

  /**
   * This property is a list of CSS classes that can be used to style the label. 
   * @deprecated with version 4.0.1 release 
   */
   @Prop() labelClass?: string = '';  
   
   /**
   * This property will visually hide the label.  A label is still required to be provided for screenreader support.
   */
   @Prop() hideLabel: boolean = false;

  /**
   * This property can be used to set a help text.
   */
  @Prop() help: string = '';

  /**
   * This property can be used to set an error. If a non-empty string is given, it will be used as the error message.
   */
  @Prop() error: string | null = null;

  /**
   * This boolean can be used to set the select as required.
   */
  @Prop() required: boolean = false;

  /**
  * This property is used to override the required indicator. By default, forms elements will show an `optional` indicator
  * but some forms are better served showing the `required` indicator instead.
  */
   @Prop() requiredInd: 'required' | 'optional' | 'none';    

  /**
   * This property can be used to set the ARIA label associated with this input (for screen readers).
   */
  @Prop() inputAriaLabel: string | null = null;

  /**
   * This property can be used to set the form field name (used when submitting the form).
   */
  @Prop() name: string = '';

  /**
   * This boolean can be used to disable the select.
   */
  @Prop() disabled: boolean = false;

  /**
   * This property can be used to set the select value.
   */
  @Prop({mutable: true, reflect: true}) value: string;
  
  /**
   * This property can be used to specifically set an ID to the input field. If an ID is not provided, it will be dynamically generated.
   */
   @Prop() inputId: string = generateId('som-input');

   /**
   * This property is used to set the tooltip text.
   */
   @Prop() tooltip: string | null = null

  /**
   * This event is emitted when when the select field gets focus.
   */
  @Event() somFocus: EventEmitter;

  /**
   * This event is emitted when when the select field loses focus.
   */
  @Event() somBlur: EventEmitter;

  /**
   * This event is emitted when the select value changes.
   */
  @Event() somChange: EventEmitter;

  htmlElement: HTMLSelectElement;

  selectEl;

  helpElementId;

  handleFocusCb;

  handleBlurCb;

  handleChangeCb;

  tooltipID: string = generateId('som-tooltipID-');

  @Watch('value')
  handleValueChange(newVal) {
    if (this.selectEl) {
      this.selectEl.value = newVal;
    }
  }

  /**
   * This public method sets focus to the select element.
   * @returns Promise will resolve after focus.
   */
  @Method()
  setFocus(): Promise<void> {
    return focusElement(this.htmlElement);
  }

  handleFocus(e) {
    this.somFocus.emit(e);
  }

  handleBlur(e) {
    this.somBlur.emit(e);
  }

  handleChange(e) {
    this.value = this.selectEl.value;
    this.somChange.emit(e);
  }

  registerEvents() {
    this.htmlElement.addEventListener('focus', this.handleFocusCb);
    this.htmlElement.addEventListener('blur', this.handleBlurCb);
    this.htmlElement.addEventListener('change', this.handleChangeCb);
  }

  unregisterEvents() {
    this.htmlElement.removeEventListener('focus', this.handleFocusCb);
    this.htmlElement.removeEventListener('blur', this.handleBlurCb);
    this.htmlElement.removeEventListener('change', this.handleChangeCb);
  }

  componentWillLoad() {
    this.helpElementId = `${this.inputId}-help`;

    this.handleFocusCb = this.handleFocus.bind(this);
    this.handleBlurCb = this.handleBlur.bind(this);
    this.handleChangeCb = this.handleChange.bind(this);
  }

  componentDidLoad() {
    this.htmlElement = this.el.querySelector(`#${this.inputId}`);
    this.registerEvents();

    if (this.value) {
      this.handleValueChange(this.value);
    }   
  }

  componentWillUnload() {
    this.unregisterEvents();
  }

  render() {
    const _isInError = isInError(this.error);
    const helpMessage = _isInError ? this.error : this.help;
    const ariaInvalid = _isInError ? { 'aria-invalid': 'true' } : {};
    const _requiredInd = getRequiredInd(h, this.required, this.requiredInd);  

    return (
      <div class='form-group'>
        <div>
          <label class={`${this.labelClass} ${this.hideLabel ? 'sr-only' : ''}`} htmlFor={this.inputId}>
            <slot name='label'>{this.label}</slot>
            {_requiredInd}
          </label>
          { this.tooltip ?
            <span>
              <som-button class='tooltip-button' button-aria-label={`${this.label} information`} button-type='clear' icon-position='only' color='primary' icon='information' aria-describedby={this.tooltipID}>
              </som-button>
              <som-tooltip horizontal-position='auto' tooltip-id={this.tooltipID}>
                {this.tooltip}
              </som-tooltip>
            </span> : null
          }
        </div>
        
        <select
          id={this.inputId}
          ref={ (el) => this.selectEl = el }
          name={this.name}
          class={`form-control ${_isInError ? 'is-invalid' : ''}`}
          disabled={this.disabled}
          required={this.required}
          aria-label={this.inputAriaLabel}
          aria-disabled={this.disabled}
          {...ariaDescribedby(this.help, this.error, this.helpElementId)}
          {...ariaInvalid}
        >
          <slot />
        </select>
        {helpMessage ? (
          <small id={this.helpElementId} class={`form-text ${_isInError ? 'text-error' : 'text-muted'}`}>
            {helpMessage}
          </small>
        ) : null}
      </div>
    );
  }
}
